import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { CityProvider } from './context/CityContext';
import AnalyticsProvider from './analytics/AnalyticsProvider';

// Pages
import PublicHome from './pages/PublicHome';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LibrarianPortal from './pages/LibrarianPortal';
import AdminDashboardPage from './pages/AdminDashboardPage';
import InstituteManagerDashboard from './pages/InstituteManagerDashboard';
import MarketingDashboard from './pages/MarketingDashboard';
import SalesDashboardPage from './pages/SalesDashboardPage';
import LibrariesPage from './pages/LibrariesPage';
import LibraryDetailPage from './pages/LibraryDetailPage';
import ScannerPage from './pages/ScannerPage';
import ProfilePage from './pages/ProfilePage';
import StudentPortal from './pages/StudentPortal';

// Student pages — rendered inside StudentPortal shell via <Outlet />
import StudentHome from './pages/student/StudentHome';
import StudentAccess from './pages/student/StudentAccess';
import StudentProfile from './pages/student/StudentProfile';
import StudentScanner from './pages/student/StudentScanner';
import FocusMode from './pages/student/FocusMode';
import StudentWallet from './pages/student/StudentWallet';
import StudentNotifications from './pages/student/StudentNotifications';
import StudentSubscriptions from './pages/student/StudentSubscriptions';
import StudentStatistics from './pages/student/StudentStatistics';
import StudentQRPass from './pages/student/StudentQRPass';
import InstituteSearch from './pages/student/InstituteSearch';
import InstituteDetail from './pages/student/InstituteDetail';
import StudentInstituteLeads from './pages/student/StudentInstituteLeads';
import CoinsPage from './pages/student/CoinsPage';

// Library Landing Page
import LibraryLandingPage from './pages/LibraryLandingPage';
// Marketing pages
import JoinUsAsLibrary from './marketing/JoinUsAsLibrary';
import JoinUsAsInstitute from './marketing/JoinUsAsInstitute';
import MarketingPage from './pages/MarketingPage';
// Marketing demos (public, no auth, no API calls)
import LibraryCrmDemo from './marketing/demos/LibraryCrmDemo';
import LibraryWebsiteDemo from './marketing/demos/LibraryWebsiteDemo';
// SEO Pages
import SeoDashboard from './pages/SeoDashboard';
import SeoLocationPage from './pages/SeoLocationPage';

// Librarian Management Pages
import LibrarianBookingsPage from './pages/librarian/LibrarianBookingsPage';
import LibraryDetailsPage from './pages/librarian/LibraryDetailsPage';
import LibrarySettingsPage from './pages/librarian/LibrarySettingsPage';
import PlansManagementPage from './pages/librarian/PlansManagementPage';
import StudentManagementPage from './pages/librarian/StudentManagementPage';
import LibrarianWallet from './pages/librarian/LibrarianWallet';
import WebsiteSettings from './pages/librarian/WebsiteSettings';

// Components
import PrivateRoute from './components/PrivateRoute';
import StudentOnlyRoute from './components/StudentOnlyRoute';
import RoleRoute from './components/RoleRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import AuthLayout from './components/auth/AuthLayout';
import AppShell from './components/AppShell';

/**
 * Catch-all router for SEO location pages (/library-in-*)
 */
function SeoLocationPageRouter() {
  const location = useLocation();
  if (location.pathname.startsWith('/library-in-')) {
    return <SeoLocationPage />;
  }
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CityProvider>
          <Router>
            {/* MotionConfig at the app root so prefers-reduced-motion is respected globally */}
            <MotionConfig reducedMotion="user">
              <AnalyticsProvider>
                <Routes>
                  {/* Library landing page (public mini-website) — excluded from AppShell/Aurora */}
                  <Route path="/l/:slug" element={<LibraryLandingPage />} />

                  {/* Marketing demos — public, no auth, no API calls, excluded from AppShell */}
                  <Route path="/demo/library-crm" element={<LibraryCrmDemo />} />
                  <Route path="/demo/library-website" element={<LibraryWebsiteDemo />} />

                  {/* All other routes share AppShell (Aurora background + Footer) */}
                  <Route element={<AppShell />}>
                    {/* Auth routes — share AuthLayout (DesktopNav + AnimatePresence) */}
                    <Route element={<AuthLayout />}>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    </Route>

                    <Route path="/libraries" element={<StudentOnlyRoute><LibrariesPage /></StudentOnlyRoute>} />
                    <Route path="/libraries/:id" element={<StudentOnlyRoute><LibraryDetailPage /></StudentOnlyRoute>} />
                    <Route path="/institutes" element={<InstituteSearch />} />
                    <Route path="/institutes/:id" element={<InstituteDetail />} />

                    {/* Marketing acquisition pages */}
                    <Route path="/join-us-as-library" element={<JoinUsAsLibrary />} />
                    <Route path="/join-us-as-institute" element={<JoinUsAsInstitute />} />

                    {/* Admin-managed generic marketing pages — must stay before the * catch-all */}
                    <Route path="/p/:slug" element={<MarketingPage />} />

                    {/* Protected non-student routes */}
                    <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
                    <Route path="/librarian-dashboard" element={<Navigate to="/librarian" replace />} />
                    <Route path="/admin-dashboard" element={<RoleRoute roles={['admin']}><AdminDashboardPage /></RoleRoute>} />
                    <Route path="/institute-manager-dashboard" element={<RoleRoute roles={['institute_manager']}><InstituteManagerDashboard /></RoleRoute>} />
                    <Route path="/marketing-dashboard" element={<RoleRoute roles={['marketing']}><MarketingDashboard /></RoleRoute>} />
                    <Route path="/sales-dashboard" element={<RoleRoute roles={['sales']}><SalesDashboardPage /></RoleRoute>} />
                    <Route path="/seo-dashboard" element={<RoleRoute roles={['seo']}><SeoDashboard /></RoleRoute>} />

                    {/* Student Portal — all /student/* routes share the shell */}
                    <Route
                      path="/student"
                      element={<PrivateRoute><StudentOnlyRoute><StudentPortal /></StudentOnlyRoute></PrivateRoute>}
                    >
                      <Route index element={<StudentHome />} />
                      <Route path="access" element={<StudentAccess />} />
                      <Route path="qr-pass" element={<StudentQRPass />} />
                      <Route path="scanner" element={<StudentScanner />} />
                      <Route path="focus-mode" element={<FocusMode />} />
                      <Route path="wallet" element={<StudentWallet />} />
                      <Route path="notifications" element={<StudentNotifications />} />
                      <Route path="subscriptions" element={<StudentSubscriptions />} />
                      <Route path="statistics" element={<StudentStatistics />} />
                      <Route path="profile" element={<StudentProfile />} />
                      <Route path="coins" element={<CoinsPage />} />
                      <Route path="libraries" element={<LibrariesPage />} />
                      <Route path="libraries/:id" element={<LibraryDetailPage />} />
                      <Route path="institutes" element={<InstituteSearch />} />
                      <Route path="institutes/:id" element={<InstituteDetail />} />
                      <Route path="institute-leads" element={<StudentInstituteLeads />} />
                    </Route>

                    {/* Legacy /my-bookings — redirected to student access in Phase 6 */}
                    <Route path="/my-bookings" element={<Navigate to="/student/access" replace />} />

                    {/* Librarian Portal */}
                    <Route path="/librarian" element={<RoleRoute roles={['librarian']}><LibrarianPortal /></RoleRoute>} />
                    <Route path="/librarian/sessions" element={<RoleRoute roles={['librarian']}><LibrarianPortal /></RoleRoute>} />
                    <Route path="/librarian/scanner" element={<RoleRoute roles={['librarian']}><LibrarianPortal /></RoleRoute>} />
                    <Route path="/librarian/analytics" element={<RoleRoute roles={['librarian']}><LibrarianPortal /></RoleRoute>} />
                    <Route path="/librarian/manage" element={<RoleRoute roles={['librarian']}><LibrarianPortal /></RoleRoute>} />

                    {/* Librarian management pages */}
                    <Route path="/librarian/students" element={<RoleRoute roles={['librarian']}><StudentManagementPage /></RoleRoute>} />
                    <Route path="/librarian/bookings" element={<RoleRoute roles={['librarian']}><LibrarianBookingsPage /></RoleRoute>} />
                    <Route path="/librarian/library-details" element={<RoleRoute roles={['librarian']}><LibraryDetailsPage /></RoleRoute>} />
                    <Route path="/librarian/settings" element={<RoleRoute roles={['librarian']}><LibrarySettingsPage /></RoleRoute>} />
                    <Route path="/librarian/plans" element={<RoleRoute roles={['librarian']}><PlansManagementPage /></RoleRoute>} />
                    <Route path="/librarian/wallet" element={<RoleRoute roles={['librarian']}><LibrarianWallet /></RoleRoute>} />
                    <Route path="/librarian/website" element={<RoleRoute roles={['librarian']}><WebsiteSettings /></RoleRoute>} />

                    {/* Legacy standalone routes */}
                    <Route path="/scanner" element={<PrivateRoute><ScannerPage /></PrivateRoute>} />
                    <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

                    {/* Public home */}
                    <Route path="/" element={<PublicHome />} />

                    {/* Legal */}
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />

                    {/* SEO location pages — must remain last before the catch-all */}
                    <Route path="*" element={<SeoLocationPageRouter />} />
                  </Route>
                </Routes>
              </AnalyticsProvider>
            </MotionConfig>
          </Router>
        </CityProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
